<template>
  <div
    v-if="!hasChildren && !option.multiExpand"
    class="select-item select-options"
    :class="{
      'select-options-splitLine': index !== 0 && splitLine,
      [`select-options-${size}`]: true,
      'select-options-selected': selected,
      disabled: option.disabled
    }"
    @click="$emit('click', $event, option)"
  >
    <div class="select-item-value" :title="option.label">
      {{ option.label }}
    </div>
    <sj-tooltip
      class="select-item-tooltip"
      v-if="option.tooltips && option.tooltipType !== 'simple'"
      placement="bottom"
    >
      <i
        slot="reference"
        :class="[
          'icon_com',
          option.tipIcon ? option.tipIcon : 'comselect_info'
        ]"
      >
      </i>
      <div slot="tooltips" v-html="option.tooltips"></div>
    </sj-tooltip>
    <div
      class="select-item-tooltip"
      v-if="option.tooltips && option.tooltipType === 'simple'"
    >
      {{ option.tooltips }}
    </div>
    <div class="select-item-num" v-if="option.num">{{ option.num }}</div>
    <sj-checkbox
      class="select-item-checkbox"
      v-if="multiple"
      :value="selected"
      size="mini"
      @change="$emit('click', null, option)"
    />
  </div>
  <sj-dropdown
    v-else
    ref="dropdown"
    :mouseOutDelay="100000000"
    :mouseOverDelay="100"
    :placement="`${extendDirection}-start`"
    trigger="hover"
    :boundariesSelector="boundariesSelector"
    :appendToBody="true"
    :arrowVisible="false"
    dropdownClass="select-item-multiExpand"
    noMargin
    style="width: 100%"
    @show="handleShow"
    @hide="handleHide"
  >
    <div
      slot="reference"
      class="select-item select-options"
      :class="{
        [`select-options-${size}`]: true,
        'select-options-selected': selected
      }"
      @click="$emit('click', null, option)"
    >
      <div style="flex: 1;" :title="option.label">{{ option.label }}</div>
      <sj-tooltip
        class="select-item-tooltip"
        v-if="option.tooltips && option.tooltipType !== 'simple'"
        placement="bottom"
      >
        <i slot="reference" class="icon_com comselect_info"></i>
        <div slot="tooltips" v-html="option.tooltips"></div>
      </sj-tooltip>
      <div
        class="select-item-tooltip"
        v-if="option.tooltips && option.tooltipType === 'simple'"
      >
        {{ option.tooltips }}
      </div>
      <i
        class="icon_com comselect_nextmenu"
        style="font-size: 12px;transform: scale(0.7);margin-left: 6px;"
        v-if="!option.multiExpand"
      />
    </div>
    <div
      slot="dropdown"
      :class="{ 'select-options-list': !option.multiExpand }"
    >
      <slot v-if="option.multiExpand"></slot>
      <SelectItem
        v-else
        ref="selectItems"
        v-for="(child, childIndex) in option.children"
        :key="child.value"
        :option="child"
        :index="childIndex"
        :splitLine="splitLine"
        :size="size"
        :extendDirection="extendDirection"
        @click="(event, option) => $emit('click', event, option)"
        @mouseenter.native="handleMouseEnter(index)"
        :boundariesSelector="boundariesSelector"
      ></SelectItem>
    </div>
  </sj-dropdown>
</template>

<script>
export default {
  name: "SelectItem",
  props: {
    option: {
      type: Object
    },
    index: Number,
    size: {
      type: String
    },
    selected: {
      type: Boolean
    },
    splitLine: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    extendDirection: {
      type: String
    },
    appendToBody: {
      type: Boolean
    },
    boundariesSelector: {
      type: String
    }
  },
  computed: {
    hasChildren() {
      return this.option.children && this.option.children.length !== 0;
    }
  },
  methods: {
    handleShow() {
      this.$emit("multiShow", this.$refs.dropdown);
    },
    handleHide() {
      this.$emit("multiHide", this.$refs.dropdown);
    },
    close() {
      if (this.hasChildren || this.option.multiExpand) {
        this.$refs.dropdown.doClose();
      }
      if (this.hasChildren) {
        this.$refs.selectItems.forEach(v => {
          v.close();
        });
      }
    },
    handleMouseEnter(selfIndex) {
      this.$refs.selectItems.forEach((v, i) => {
        if (i === selfIndex) return;
        v.close();
      });
    }
  }
};
</script>

<style lang="less" scoped>
@--select-font-tiny: 10px;
@--select-font-small: 12px;
@--select-font-base: 14px;

@--select-selection-background-color: @color-gray-2;
@--select-category-font-color: @color-normal;
@--select-selection-font-color: @color-highlight;
@--select-selection-font-weight: @regular-weight;
@--select-line-color: @color-gray-2;

.select-options {
  position: relative;
  display: flex;
  align-items: center;
}

.select-options-list {
  position: relative;
  padding: 6px 0;
  max-height: 300px;
  overflow-x: visible;
  overflow-y: auto;
}

.select-item {
  width: 100%;
  /*min-width: 120px;*/
  box-sizing: border-box;
  text-align: left;
  user-select: none;
  padding: 4px 12px;
  font-size: 12px;
  line-height: 22px;
  color: @color-normal;
  overflow: hidden;

  &:hover {
    & + .select-options-splitLine {
      &::before {
        height: 0;
      }
    }
  }

  &.disabled {
    cursor: not-allowed !important;

    .select-item-value {
      color: @color-light;
    }
    .select-item-tooltip {
      color: @color-light;
    }

    /deep/ .sj-checkbox-box {
      background-color: @color-gray-2;
      border-color: @color-gray-3 !important;
    }
  }

  .select-item-value {
    flex-grow: 1;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .select-item-checkbox {
    flex-shrink: 0;
    margin-left: 5px;
  }

  .select-item-tooltip {
    flex-shrink: 0;
  }
  .select-item-num {
    .text("caption", "normal");
    &:hover {
      .text("caption", "normal");
    }
  }
}

.select-options:hover {
  cursor: pointer;
  background-color: @--select-selection-background-color;
  color: @--select-selection-font-color;
}

.select-options-selected {
  background-color: @--select-selection-background-color;
  color: @--select-selection-font-color;
  font-weight: @--select-selection-font-weight;

  & + .select-options-splitLine {
    &::before {
      height: 0;
    }
  }

  &.select-options-splitLine {
    &::before {
      height: 0;
    }
  }
}

.select-options-splitLine {
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 12px;
    right: 12px;
    height: 1px;
    background-color: @color-gray-4;
  }

  &:hover {
    &::before {
      height: 0;
    }
  }
}

// .select-options-small {
//   padding: 6px 16px;
//   font-size: @--select-font-small;
// }

// .select-options-middle {
//   padding: 8px 16px;
//   font-size: @--select-font-base;
// }

// .select-options-large {
//   padding: 10px 16px;
//   font-size: @--select-font-base;
// }
body {
  span {
    /deep/ .sj-dropdown {
      margin-left: 0;
      margin-right: 0;
      border: none;

      transition: none;

      .popper__arrow {
        display: none;
      }
    }
  }
}
.icon-icon_info {
  .text("tiny", "subinfo");
  padding: 0 5px;
}
</style>
